<template>
  <div v-if="rollups.length" class="space-y-3">
    <div
      v-for="r in rollups"
      :key="r.id"
      class="rounded-xl border border-gray-200 bg-white overflow-hidden"
    >
      <!-- Header -->
      <div class="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div class="flex items-start gap-3 min-w-0">
          <div class="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
            <font-awesome-icon :icon="['fas', 'diagram-project']" class="text-primary-700 text-sm" />
          </div>
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-sm font-semibold text-gray-900">
                {{ groupingName }} · {{ r.title }}
              </h3>
              <span v-if="r.primary_metric_rate != null" class="text-sm font-semibold" :style="{ color: paceHex(r.trajectory) }">
                {{ fmtRate(r.primary_metric_rate) }}
              </span>
            </div>
            <div class="text-xs text-gray-400 mt-0.5">
              Conversation graph · {{ r.summarizes_count }} {{ childNoun }} · {{ relTime(r.updated_at) }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <span
            v-if="momentumBadge(r)"
            class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
            :style="{ background: momentumBadge(r).tint, color: momentumBadge(r).hex }"
          >
            <font-awesome-icon :icon="['fas', momentumBadge(r).icon]" />{{ momentumBadge(r).label }}
          </span>
          <span
            v-if="paceBadge(r)"
            class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
            :style="{ background: paceBadge(r).tint, color: paceBadge(r).hex }"
          >Pace: {{ paceBadge(r).label.toLowerCase() }}</span>
        </div>
      </div>

      <!-- Summary -->
      <p v-if="r.description" class="px-5 pb-3 text-xs text-gray-700 leading-relaxed">{{ r.description }}</p>

      <!-- Body: drivers/blockers  |  conversation signal -->
      <div class="grid grid-cols-1 lg:grid-cols-2 border-t border-gray-100">
        <div class="px-5 py-4 lg:border-r border-gray-100">
          <div class="text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-2.5">Drivers &amp; Blockers</div>
          <div class="space-y-2.5">
            <div v-if="r.drives" class="flex items-start gap-2.5">
              <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold shrink-0" style="background:#E7F3ED;color:#389A6C">
                <font-awesome-icon :icon="['fas', 'arrow-up']" />DRIVES
              </span>
              <span class="text-xs text-gray-700">{{ r.drives.label }}<span v-if="r.drives.detail" class="text-gray-400"> · {{ r.drives.detail }}</span></span>
            </div>
            <div v-if="r.blocks" class="flex items-start gap-2.5">
              <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold shrink-0" style="background:#FAE8E8;color:#D53D3D">
                <font-awesome-icon :icon="['fas', 'ban']" />BLOCKS
              </span>
              <span class="text-xs text-gray-700">{{ r.blocks.label }}<span v-if="r.blocks.detail" class="text-gray-400"> · {{ r.blocks.detail }}</span></span>
            </div>
            <div v-if="r.addresses" class="flex items-start gap-2.5">
              <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold shrink-0" style="background:#E2F1F3;color:#0E8C9E">
                <font-awesome-icon :icon="['fas', 'wrench']" />ADDRESSES
              </span>
              <span class="text-xs text-gray-700">{{ r.addresses.label }}<span v-if="r.addresses.detail" class="text-gray-400"> · {{ r.addresses.detail }}</span></span>
            </div>
            <div v-if="!r.drives && !r.blocks && !r.addresses" class="text-xs text-gray-300">No dominant drivers or blockers across the cohort.</div>
          </div>
        </div>

        <div class="px-5 py-4">
          <div class="text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-2.5">Conversation Signal</div>
          <template v-if="signalQuote(r)">
            <p class="text-xs text-gray-700 italic leading-relaxed">“{{ signalQuote(r).quote }}”</p>
            <div class="flex items-center gap-2 mt-2 text-xs">
              <span v-if="sentimentPill(signalQuote(r))" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium" :style="sentimentPill(signalQuote(r)).style">
                <font-awesome-icon :icon="['fas', sentimentPill(signalQuote(r)).icon]" />{{ sentimentPill(signalQuote(r)).label }}
              </span>
              <span v-if="signalQuote(r).practice_name" class="text-gray-400 sensitive">{{ signalQuote(r).practice_name }}</span>
            </div>
          </template>
          <p v-else class="text-xs text-gray-300">No representative quote for this cohort yet.</p>

          <!-- Reconciliation line -->
          <div v-if="r.reconciliation_note" class="mt-3 flex items-start gap-2 rounded-lg bg-secondary-100 px-3 py-2">
            <font-awesome-icon :icon="['fas', 'circle-check']" class="text-green-dark mt-0.5" style="color:#389A6C" />
            <span class="text-xs text-gray-700">{{ r.reconciliation_note }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faDiagramProject, faArrowUp, faBan, faWrench, faCircleCheck,
  faArrowTrendUp, faArrowTrendDown, faArrowRight, faFaceSmile, faFaceMeh, faFaceFrown,
} from "@fortawesome/free-solid-svg-icons";
import {
  HEALTH, NEUTRAL, MOMENTUM, trajectoryBadge, fetchMetricSeries, deriveMetricSeries,
} from "@/components/EngagementTracker/engagementTrackerHelpers.js";

library.add(faDiagramProject, faArrowUp, faBan, faWrench, faCircleCheck,
  faArrowTrendUp, faArrowTrendDown, faArrowRight, faFaceSmile, faFaceMeh, faFaceFrown);

// Cohort Engagement Rollup card — the narrative summary of ONE initiative rolled up across a
// grouping's child practices, built from the ROLLUP EngagementGraphNode the rollup analysis
// materializes (rollup_scope != null). Reuses the Engagement Tracker color language so pace /
// momentum / sentiment read identically to the practice-level tracker. One card per initiative.
export default {
  name: "CohortRollupCard",
  components: { FontAwesomeIcon },
  props: {
    // The grouping entity (Pod / Contract / Region / tenant) whose rollups to show.
    entityId: { type: Number, required: true },
    groupingName: { type: String, default: "" },
    // Filter to specific initiative_keys (optional; default shows all rollups present).
    initiatives: { type: Array, default: () => [] },
  },
  emits: ["loading"],
  data() {
    return { rollups: [] };
  },
  computed: {
    childNoun() {
      // "practices" for a leaf-summarizing rollup; kept generic since a higher rollup summarizes
      // lower rollups — the count is what matters, the noun is cosmetic.
      return "practices";
    },
  },
  watch: {
    entityId() { this.fetch(); },
    // Re-filter (and re-fetch, since the initiative filter is applied during fetch) when the
    // focused initiative changes — e.g. clicking a different metric bar while the drill-in is open.
    initiatives() { this.fetch(); },
  },
  mounted() { this.fetch(); },
  methods: {
    async fetch() {
      if (!this.entityId) return;
      this.$emit("loading", true);
      try {
        const { data } = await axios.get("/api/engagement-graph-nodes/rollups/", {
          params: { entity_id: this.entityId },
        });
        let rows = (data.rollups || []).map((n) => this.shape(n));
        if (this.initiatives.length) {
          rows = rows.filter((r) => this.initiatives.includes(r.initiative_key));
        }
        this.rollups = rows;
        await this.fetchHeadlineRates();   // fill the header % per metric-grounded rollup
      } catch (e) {
        console.error("Error fetching cohort rollups:", e);
        this.rollups = [];
      } finally {
        this.$emit("loading", false);
      }
    },
    // Headline % = the rollup's primary metric rolled up to THIS grouping entity — the same
    // precompute path the Insights Performance Trend chart + the Engagement Tracker use
    // (fetchMetricSeries → deriveMetricSeries, current-year latest rate). Non-metric rollups
    // (no primary_metric_id, e.g. Contract & Payments) simply show no %.
    async fetchHeadlineRates() {
      const year = new Date().getFullYear();
      await Promise.all(this.rollups.map(async (r) => {
        if (r.primary_metric_id == null) return;
        try {
          const { meta, results } = await fetchMetricSeries(r.primary_metric_id, this.entityId, year);
          const series = deriveMetricSeries(meta, results, year);
          r.primary_metric_rate = series.latest;
        } catch (e) {
          console.warn("Rollup headline rate fetch failed", r.primary_metric_id, e);
        }
      }));
      // Vue 3 reactivity: replace the array so the mutated rows re-render.
      this.rollups = [...this.rollups];
    },
    // Flatten the node's metadata rollup fields + evidence onto the card's shape.
    shape(n) {
      const m = n.metadata || {};
      const quote = (n.evidence || []).find((e) => e.type === "transcript_quote");
      return {
        id: n.id,
        initiative_key: n.initiative_key,
        title: n.title,
        description: n.description,
        trajectory: n.trajectory,
        updated_at: n.updated_at,
        summarizes_count: n.summarizes_count || 0,
        drives: m.drives || null,
        blocks: m.blocks || null,
        addresses: m.addresses || null,
        momentum: m.momentum || null,
        reconciliation_note: m.reconciliation_note || null,
        primary_metric_id: n.primary_metric_id != null ? n.primary_metric_id : null,
        primary_metric_rate: null, // filled by fetchHeadlineRates() (the rolled-up % at this grouping)
        _quote: quote ? { quote: (quote.data && quote.data.quote) || "", practice_name: (quote.data && quote.data.practice_name) || "", sentiment: (quote.data && quote.data.sentiment) || null } : null,
      };
    },
    signalQuote(r) { return r._quote && r._quote.quote ? r._quote : null; },
    paceHex(traj) { return (HEALTH[traj] || NEUTRAL).hex; },
    paceBadge(r) { return trajectoryBadge(r.trajectory); },
    momentumBadge(r) {
      const key = { rising: "up", steady: "flat", declining: "down" }[r.momentum];
      if (!key) return null;
      const m = MOMENTUM[key];
      return { label: m.label, icon: m.icon, hex: m.hex, tint: m.tint };
    },
    sentimentPill(q) {
      const map = {
        positive: { label: "Positive", icon: "face-smile" },
        neutral: { label: "Neutral", icon: "face-meh" },
        negative: { label: "Negative", icon: "face-frown" },
      };
      const s = map[q.sentiment];
      if (!s) return null;
      const tint = q.sentiment === "positive" ? { background: "#E7F3ED", color: "#389A6C" }
        : q.sentiment === "negative" ? { background: "#FAE8E8", color: "#D53D3D" }
        : { background: "#f3f4f6", color: "#374151" };
      return { label: s.label, icon: s.icon, style: tint };
    },
    fmtRate(v) { return v == null ? "—" : `${(v * 100).toFixed(0)}%`; },
    relTime(iso) {
      if (!iso) return "";
      const diff = Date.now() - new Date(iso).getTime();
      const h = Math.floor(diff / 3600000);
      if (h < 1) return "updated just now";
      if (h < 24) return `updated ${h}h ago`;
      return `updated ${Math.floor(h / 24)}d ago`;
    },
  },
};
</script>
